import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Play, RefreshCw, CheckCircle2, FlaskConical } from "lucide-react";
import { useState } from "react";
import { api } from "@/lib/api";
import { ErrorState } from "@/components/empty-state";

export const Route = createFileRoute("/admin/gamesplanet")({
  component: AdminGamesplanet,
});

type Region = "DE" | "FR" | "UK" | "US";
const REGIONS: Region[] = ["DE", "FR", "UK", "US"];

function AdminGamesplanet() {
  const qc = useQueryClient();
  const [limit, setLimit] = useState<string>("");
  const [region, setRegion] = useState<Region | "">("");
  const status = useQuery({
    queryKey: ["admin-gamesplanet-status"],
    queryFn: () => api.adminGamesplanetStatus(),
    refetchInterval: 4000,
  });
  const errors = useQuery({
    queryKey: ["admin-gamesplanet-errors"],
    queryFn: () => api.adminImportErrors(),
    refetchInterval: 8000,
  });
  const run = useMutation({
    mutationFn: (opts: { dryRun?: boolean }) =>
      api.adminImportGamesplanet({
        dryRun: opts.dryRun,
        limit: limit ? Number(limit) : undefined,
        region: region || undefined,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-gamesplanet-status"] }),
  });

  const data = status.data?.data;
  const jobs = data?.jobs ?? [];
  const isRunning = jobs[0]?.status === "running" || run.isPending;

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold">Gamesplanet import</h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            Imports Gamesplanet product feeds across DE, FR, UK and US regions. Matching runs
            against the existing Steam catalog; DLCs, upgrades and expansions are never mapped
            to the base game. Affiliate tag <code>?ref=nexkey</code> is enforced on every URL.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={region}
            onChange={(e) => setRegion(e.target.value as Region | "")}
            className="rounded-md border border-border bg-surface px-3 py-2 text-sm"
          >
            <option value="">All regions</option>
            {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <input
            type="number"
            min={1}
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            placeholder="Limit (optional)"
            className="w-40 rounded-md border border-border bg-surface px-3 py-2 text-sm"
          />
          <button
            onClick={() => run.mutate({ dryRun: true })}
            disabled={isRunning}
            className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-3 py-2 text-sm font-semibold disabled:opacity-50"
          >
            <FlaskConical className="h-4 w-4" /> Dry run
          </button>
          <button
            onClick={() => run.mutate({})}
            disabled={isRunning}
            className="inline-flex items-center gap-1 rounded-md bg-primary-gradient px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
          >
            <Play className="h-4 w-4" /> {isRunning ? "Running…" : "Start import"}
          </button>
        </div>
      </header>

      {run.isError && <ErrorState message={(run.error as Error).message} />}

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className="font-display text-lg font-semibold">Regions</h3>
          <div className="mt-2 space-y-1 text-xs">
            {(data?.regions ?? []).map((r) => (
              <div key={r.region} className="flex items-center justify-between gap-2">
                <span className="font-mono">{r.region}</span>
                <span className="text-muted-foreground">{r.currency}</span>
              </div>
            ))}
            {!data?.regions?.length && <div className="text-muted-foreground">—</div>}
          </div>
          <div className="mt-3 flex items-center gap-1 text-xs text-success">
            <CheckCircle2 className="h-3 w-3" /> affiliate <span className="font-mono">?ref={data?.affiliateTag ?? "nexkey"}</span>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className="font-display text-lg font-semibold">Shop</h3>
          <div className="mt-1 text-sm">{data?.shop?.name ?? "Not created yet — will be created on first non-dry run"}</div>
          <div className="text-xs text-muted-foreground">{data?.offerCount ?? 0} active offers</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className="font-display text-lg font-semibold">Last run</h3>
          {jobs[0] ? (
            <div className="mt-1 space-y-1 text-xs">
              <div>{new Date(jobs[0].startedAt).toLocaleString()}</div>
              <div>Status: <span className={
                jobs[0].status === "completed" ? "text-success" :
                jobs[0].status === "running" ? "text-primary" :
                jobs[0].status === "failed" ? "text-destructive" : "text-muted-foreground"
              }>{jobs[0].status}</span></div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 pt-1">
                <span className="text-muted-foreground">Matched</span>
                <span className="text-right font-mono">{(jobs[0].matchedItems ?? 0).toLocaleString()}</span>
                <span className="text-muted-foreground">Unmatched</span>
                <span className="text-right font-mono">{(jobs[0].unmatchedItems ?? 0).toLocaleString()}</span>
                <span className="text-muted-foreground">Failed</span>
                <span className="text-right font-mono text-destructive">{jobs[0].failedItems.toLocaleString()}</span>
                <span className="text-muted-foreground">Imported</span>
                <span className="text-right font-mono text-success">{jobs[0].importedItems.toLocaleString()}</span>
                <span className="text-muted-foreground">Total</span>
                <span className="text-right font-mono">{jobs[0].totalItems.toLocaleString()}</span>
              </div>
              {jobs[0].errorMessage && (
                <p className="rounded bg-muted p-2 text-[11px] text-muted-foreground">{jobs[0].errorMessage}</p>
              )}
            </div>
          ) : (
            <div className="mt-1 text-xs text-muted-foreground">No runs yet</div>
          )}
          <button
            onClick={() => status.refetch()}
            className="mt-3 inline-flex items-center gap-1 rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-semibold"
          >
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>
      </div>

      <div>
        <h2 className="mb-3 font-display text-lg font-semibold">Recent jobs</h2>
        {status.isError && <ErrorState message={(status.error as Error).message} />}
        {jobs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No Gamesplanet imports have run yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-surface text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Started</th>
                  <th className="px-4 py-3">Finished</th>
                  <th className="px-4 py-3 text-right">Matched</th>
                  <th className="px-4 py-3 text-right">Unmatched</th>
                  <th className="px-4 py-3 text-right">Failed</th>
                  <th className="px-4 py-3 text-right">Imported</th>
                  <th className="px-4 py-3 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={j.id} className="border-t border-border/60">
                    <td className="px-4 py-2">{j.source}</td>
                    <td className="px-4 py-2">
                      <span className={
                        j.status === "completed" ? "text-success" :
                        j.status === "running" ? "text-primary" :
                        j.status === "failed" ? "text-destructive" : "text-muted-foreground"
                      }>{j.status}</span>
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">{new Date(j.startedAt).toLocaleString()}</td>
                    <td className="px-4 py-2 text-muted-foreground">{j.finishedAt ? new Date(j.finishedAt).toLocaleString() : "—"}</td>
                    <td className="px-4 py-2 text-right font-mono">{(j.matchedItems ?? 0).toLocaleString()}</td>
                    <td className="px-4 py-2 text-right font-mono">{(j.unmatchedItems ?? 0).toLocaleString()}</td>
                    <td className="px-4 py-2 text-right font-mono text-destructive">{j.failedItems.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right font-mono text-success">{j.importedItems.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right font-mono">{j.totalItems.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <h2 className="mb-3 font-display text-lg font-semibold">Recent unmatched / failed products</h2>
        {errors.isSuccess && errors.data.data.length === 0 && (
          <p className="text-sm text-muted-foreground">No errors logged.</p>
        )}
        {errors.isSuccess && errors.data.data.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-surface text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Reason</th>
                </tr>
              </thead>
              <tbody>
                {errors.data.data.slice(0, 50).map((e: { id: string; createdAt: string; rawPayload?: { title?: string; name?: string }; errorMessage?: string }) => (
                  <tr key={e.id} className="border-t border-border/60">
                    <td className="px-4 py-2 text-muted-foreground">{new Date(e.createdAt).toLocaleString()}</td>
                    <td className="px-4 py-2 text-muted-foreground">{e.rawPayload?.title ?? e.rawPayload?.name ?? "—"}</td>
                    <td className="px-4 py-2 text-destructive">{e.errorMessage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
